// src/components/analytics/MlHealthDashboard.js
// FlowSense MOW+ — Enterprise ML Health & Telemetry Dashboard
// Version: 2.1 — WebSocket circuit integration + retrain/warmup/reset actions
// ─────────────────────────────────────────────────────────────────────────────
// MERGE NOTES (v2.0 → v2.1):
//
//   NEW IMPORTS:
//   - MdOutlineWhatshot, MdPowerSettingsNew added to react-icons/md
//   - webSocketService imported for live circuit-breaker event stream
//
//   DRIFTALERTBANNER:
//   - onRetrain + isRetraining props added; "Retrain Now" button wired to
//     handleRetrainDrifted for 1-click background model retraining
//
//   STATE:
//   - isRetraining: tracks in-flight /ml/models/retrain-drifted request
//   - liveCircuitState: WS-driven circuit state ('CLOSED' | 'OPEN'), replaces
//     the static circuitClosed boolean derived from healthData
//
//   FETCHTELEMETRY:
//   - Step 3 added: GET /workorders/watchdog/circuit to seed liveCircuitState
//     on initial load (WS then keeps it current between polls)
//
//   WEBSOCKET:
//   - Subscribes to 'watchdog_events'; handles circuit_open / circuit_closed
//     payload types — updates liveCircuitState and fires toast immediately
//     without waiting for the next 60s poll
//
//   NEW HANDLERS:
//   - handleResetCircuit: POST /workorders/watchdog/circuit/reset
//   - handleWarmUpModels: POST /ml/cache/warmup
//   - handleRetrainDrifted: POST /ml/models/retrain-drifted
//
//   HANDLEEXPORT: liveCircuitState added to JSON snapshot
//
//   KPI CARD: Circuit card reads liveCircuitState (live WS) instead of static
//     healthData?.triage?.classifier_loaded boolean
//
//   PYTHON CACHE PANEL: Warm Up Models + Reset ML Circuit Breaker buttons added
//
//   CORE API CALLS: ZERO CHANGES
//   api.get('/ml/health'), api.get('/ml/cache/stats'),
//   api.post('/ml/cache/reset', { prefix }),
//   api.post('/ml/cache/invalidate', { model_type })
// ─────────────────────────────────────────────────────────────────────────────
// --- UPDATE (v2.2 · FlowSense AI Explainer + real-data pass)
//   • Websocket fix: the listener gets { type:'watchdog_events', payload:{ type:'circuit_open' } };
//     it read the outer type, so circuit changes never showed until the next poll.
//   • Circuit state starts as UNKNOWN (was CLOSED before anything was fetched) and HALF_OPEN is shown.
//   • No invented numbers: average latency / hit rate with no metrics show "—" (were 0.0 ms / 0 %);
//     the hit rate averages only the caches that reported one.
//   • Drift: uses the engine's own drift flag/score when it sends one; otherwise the old rule
//     (baseline stored and error rate > 10 %) and the banner says that is what it is.
//   • "Retrain Now" retrains every flagged model (was only the first); per-row Retrain button.
//   • "Explain ML health" FlowSense AI explainer built from /ml/health + cache stats + circuit.
// --- END UPDATE

import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  Box, Flex, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  StatArrow, Heading, Text, Badge, Icon, HStack, VStack, Progress,
  Button, useColorModeValue, useToast, Tooltip, Table, Thead, Tbody,
  Tr, Th, Td, TableContainer, Skeleton, Alert, AlertIcon, AlertTitle,
  AlertDescription, Divider, IconButton, Code, CloseButton, Collapse,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, useDisclosure, Select, Tag, TagLabel, TagLeftIcon,
  ScaleFade, SlideFade, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon, Wrap, WrapItem,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import ReactApexChart from 'react-apexcharts';
import {
  MdMemory, MdStorage, MdSpeed, MdWarning, MdCheckCircle, MdError,
  MdAutorenew, MdDeleteSweep, MdOutlineModelTraining, MdOutlineHealthAndSafety,
  MdSensorsOff, MdTimeline, MdInfoOutline, MdBugReport, MdDownload,
  MdSettings, MdRefresh, MdExpandMore,
  // --- UPDATE: New action icons ---
  MdPowerSettingsNew, MdOutlineWhatshot,
  // --- END UPDATE ---
} from 'react-icons/md';
import { FiAlertTriangle, FiWifi, FiWifiOff, FiActivity } from 'react-icons/fi';
import { FaChartBar } from 'react-icons/fa';

import api from '../../services/api';
// --- UPDATE: WebSocket service for live circuit events ---
import webSocketService from '../../services/websocketService';
// --- END UPDATE ---
// --- UPDATE (v2.2)
import { liveEventBody } from '../../services/analyticsService';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer';

const CIRCUIT_COLOR = { CLOSED: 'green', HALF_OPEN: 'yellow', OPEN: 'red', UNKNOWN: 'gray' };
const CIRCUIT_TEXT = {
  CLOSED: 'Routing traffic normally',
  HALF_OPEN: 'Probing — next call decides',
  OPEN: 'Fallback mode active',
  UNKNOWN: 'Circuit state not reported',
};
/** Engine drift flag when present, else the legacy error-rate rule. */
const modelHasDrift = (details = {}, metrics = {}) => {
  if (typeof details.drift_detected === 'boolean') return details.drift_detected;
  if (typeof details.drift === 'boolean') return details.drift;
  if (Number.isFinite(Number(details.drift_score)) && Number.isFinite(Number(details.drift_threshold))) {
    return Number(details.drift_score) > Number(details.drift_threshold);
  }
  return !!details.baseline_stored && (Number(metrics.error_rate) || 0) > 0.1;
};
// --- END UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const POLL_OPTIONS = [
  { label: '30s',  value: 30_000  },
  { label: '1m',   value: 60_000  },
  { label: '2m',   value: 120_000 },
  { label: '5m',   value: 300_000 },
];

const LATENCY_THRESHOLDS = { good: 150, warn: 400 };

const getLatencyColor = (ms) => {
  if (ms == null)                      return 'gray';
  if (ms <= LATENCY_THRESHOLDS.good)   return 'green';
  if (ms <= LATENCY_THRESHOLDS.warn)   return 'orange';
  return 'red';
};

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const MotionBox = motion(Box);

const pulseAnimation = {
  scale:   [1, 1.12, 1],
  opacity: [0.7, 1, 0.7],
  transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' },
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY HELPERS  (preserved)
// ─────────────────────────────────────────────────────────────────────────────

const formatPercentage = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge = React.memo(({ status }) => {
  switch (status?.toLowerCase()) {
    case 'ready': case 'loaded': case 'closed': case 'active':
      return <Badge colorScheme="green"  px={2} py={1} borderRadius="full">{status.toUpperCase()}</Badge>;
    case 'half_open': case 'warning': case 'degraded':
      return <Badge colorScheme="yellow" px={2} py={1} borderRadius="full">{status.toUpperCase()}</Badge>;
    case 'open': case 'error': case 'not_trained': case 'not_available':
      return <Badge colorScheme="red"    px={2} py={1} borderRadius="full">{status.toUpperCase()}</Badge>;
    default:
      return <Badge colorScheme="gray"   px={2} py={1} borderRadius="full">{status?.toUpperCase() || 'UNKNOWN'}</Badge>;
  }
});
StatusBadge.displayName = 'StatusBadge';

const ConfirmModal = React.memo(({ isOpen, onClose, action, isLoading }) => {
  if (!action) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiAlertTriangle} color="orange.500" />
            <Text>{action.title}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600">{action.description}</Text>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>Cancel</Button>
          <Button colorScheme={action.scheme || 'red'} onClick={action.onConfirm} isLoading={isLoading} loadingText="Processing...">
            {action.confirmLabel || 'Confirm'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
ConfirmModal.displayName = 'ConfirmModal';

// --- UPDATE: DriftAlertBanner — onRetrain + isRetraining props added;
//   "Retrain Now" button wired to handleRetrainDrifted. ---
const DriftAlertBanner = React.memo(({ driftedModels, onDismiss, onRetrain, isRetraining }) => {
  const bg = useColorModeValue('orange.50', 'rgba(251,211,141,0.1)');
  if (!driftedModels || driftedModels.length === 0) return null;

  return (
    <ScaleFade initialScale={0.95} in>
      <Flex
        align="center" justify="space-between"
        bg={bg} borderWidth="1px" borderColor="orange.300"
        borderRadius="xl" px={5} py={3} mb={4} gap={3} flexWrap="wrap"
      >
        <HStack spacing={3}>
          <MotionBox animate={pulseAnimation}>
            <Icon as={FiAlertTriangle} color="orange.500" boxSize={5} />
          </MotionBox>
          <Box>
            <Text fontWeight="bold" color="orange.700" fontSize="sm">
              Drift / error-rate alert on {driftedModels.length} model{driftedModels.length > 1 ? 's' : ''}
            </Text>
            <Text fontSize="xs" color="orange.600">
              Affected: {driftedModels.join(', ')} — engine drift flag, or error rate above 10 % against a stored baseline.
            </Text>
          </Box>
        </HStack>
        <HStack spacing={2}>
          <Button
            size="sm" colorScheme="orange" variant="solid"
            isLoading={isRetraining} loadingText="Retraining..."
            onClick={() => onRetrain && onRetrain(driftedModels)}
          >
            Retrain {driftedModels.length > 1 ? `all ${driftedModels.length}` : 'Now'}
          </Button>
          <CloseButton size="sm" color="orange.400" onClick={onDismiss} />
        </HStack>
      </Flex>
    </ScaleFade>
  );
});
DriftAlertBanner.displayName = 'DriftAlertBanner';
// --- END UPDATE ---

const RefreshCountdown = React.memo(({ secondsLeft, pollInterval }) => {
  const pct    = (secondsLeft / (pollInterval / 1000)) * 100;
  const urgent = secondsLeft <= 5;

  return (
    <Tooltip label={`Next auto-refresh in ${secondsLeft}s`} hasArrow placement="top">
      <Box cursor="default">
        <HStack justify="space-between" mb={1}>
          <Text fontSize="10px" color="gray.400" fontWeight="semibold" textTransform="uppercase">Auto-refresh</Text>
          <Text fontSize="10px" color={urgent ? 'orange.400' : 'gray.400'} fontWeight="bold">{secondsLeft}s</Text>
        </HStack>
        <Progress value={pct} size="xs" colorScheme={urgent ? 'orange' : 'blue'} borderRadius="full" bg="gray.100" transition="value 1s linear" />
      </Box>
    </Tooltip>
  );
});
RefreshCountdown.displayName = 'RefreshCountdown';

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const MlHealthDashboard = ({ dateRange }) => {

  // ── Theme ──────────────────────────────────────────────────────────────────
  const cardBg       = useColorModeValue('white',   'gray.800');
  const borderColor  = useColorModeValue('gray.200', 'gray.700');
  const textColor    = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');
  const theadBg      = useColorModeValue('gray.50',  'gray.700');
  const tableHoverBg = useColorModeValue('gray.50',  'gray.700');
  const debugBg      = useColorModeValue('gray.50',  'gray.700');
  const mutedColor   = useColorModeValue('gray.500', 'gray.400');
  const chartText    = useColorModeValue('#1A202C',  '#FFFFFF');
  const chartGrid    = useColorModeValue('#E2E8F0',  '#4A5568');

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading,         setLoading]         = useState(true);
  const [sectionLoading,  setSectionLoading]  = useState(false);
  const [error,           setError]           = useState(null);
  const [healthData,      setHealthData]      = useState(null);
  const [nodeCacheStats,  setNodeCacheStats]  = useState(null);
  const [isFlushing,      setIsFlushing]      = useState(false);
  // --- UPDATE: isRetraining ---
  const [isRetraining,    setIsRetraining]    = useState(false);
  // --- END UPDATE ---
  const [pollInterval,    setPollInterval]    = useState(60_000);
  const [countdown,       setCountdown]       = useState(60);
  const [pendingAction,   setPendingAction]   = useState(null);
  const [driftDismissed,  setDriftDismissed]  = useState(false);
  const [showDebug,       setShowDebug]       = useState(false);
  const [debugLogs,       setDebugLogs]       = useState([]);
  const [perfMetrics,     setPerfMetrics]     = useState({ renderTime: 0, lastUpdate: null });
  const [expandSuspended, setExpandSuspended] = useState(false);
  // --- UPDATE: liveCircuitState — WS-driven, seeds from HTTP poll ---
  const [liveCircuitState, setLiveCircuitState] = useState('UNKNOWN'); // --- UPDATE (v2.2): was CLOSED before any data
  // --- END UPDATE ---

  // ── Refs ───────────────────────────────────────────────────────────────────
  const isMounted      = useRef(true);
  const renderCount    = useRef(0);
  const intervalRef    = useRef(null);
  const countdownRef   = useRef(null);

  renderCount.current += 1;

  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const toast = useToast();

  // ── Debug Logger ───────────────────────────────────────────────────────────
  const addLog = useCallback((message, type = 'info', details = null) => {
    if (!showDebug) return;
    let safeDetails = details;
    try {
      if (details instanceof Error) {
        safeDetails = { message: details.message, name: details.name };
      } else if (details && typeof details === 'object') {
        safeDetails = JSON.parse(JSON.stringify(details, (k, v) =>
          typeof v === 'function' ? '[Function]' : v
        ));
      }
    } catch { safeDetails = '[Unserializable]'; }
    setDebugLogs(prev => [{ timestamp: new Date().toISOString(), type, message, details: safeDetails }, ...prev].slice(0, 100));
  }, [showDebug]);

  // ── Countdown timer ────────────────────────────────────────────────────────
  const resetCountdown = useCallback(() => {
    setCountdown(Math.floor(pollInterval / 1000));
  }, [pollInterval]);

  useEffect(() => {
    resetCountdown();
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? Math.floor(pollInterval / 1000) : prev - 1));
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [pollInterval, resetCountdown]);

  // ── CORE: fetchTelemetry ───────────────────────────────────────────────────
  const fetchTelemetry = useCallback(async (isPartial = false) => {
    if (!isMounted.current) return;
    const startTime = performance.now();

    if (isPartial) setSectionLoading(true);
    else if (!healthData) setLoading(true);

    setError(null);
    addLog(`fetchTelemetry called (partial=${isPartial})`, 'info');

    try {
      // 1. Fetch Python ML Engine Health
      const mlRes = await api.get('/ml/health');
      if (!isMounted.current) return;
      if (mlRes.data?.success) {
        setHealthData(mlRes.data.data);
        setDriftDismissed(false);
        addLog('ML health payload received', 'success', { keys: Object.keys(mlRes.data.data || {}) });
      } else {
        throw new Error('ML API returned unsuccessful payload.');
      }

      // 2. Fetch Node.js Controller Cache Stats
      const nodeRes = await api.get('/ml/cache/stats');
      if (!isMounted.current) return;
      if (nodeRes.data?.success) {
        setNodeCacheStats(nodeRes.data.data);
        addLog('Node cache stats received', 'success');
      } else {
        addLog('Node cache stats returned non-success', 'warn');
      }

      // --- UPDATE: Step 3 — seed liveCircuitState from HTTP.
      //   WebSocket keeps it live between polls; this seeds it on refresh. ---
      const circuitRes = await api.get('/workorders/watchdog/circuit').catch(() => null);
      if (circuitRes?.data?.success && isMounted.current) {
        setLiveCircuitState(String(circuitRes.data.data.state || 'UNKNOWN').toUpperCase()); // --- UPDATE (v2.2)
        addLog('Circuit state synced', 'info', { state: circuitRes.data.data.state });
      }
      // --- END UPDATE ---

      const elapsed = performance.now() - startTime;
      setPerfMetrics({ renderTime: elapsed.toFixed(1), lastUpdate: new Date() });

    } catch (err) {
      if (!isMounted.current) return;
      const msg = err.response?.data?.message || err.message || 'Failed to connect to ML Engine telemetry.';
      addLog(`fetchTelemetry error: ${msg}`, 'error', err);
      console.error('[MlHealthDashboard] Fetch error:', err);
      if (!healthData) setError(msg);
      else toast({ title: 'Telemetry Refresh Failed', description: msg, status: 'warning', duration: 4000, isClosable: true });
    } finally {
      if (isMounted.current) { setLoading(false); setSectionLoading(false); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLog, toast]);

  // ── Mount / unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    isMounted.current = true;
    fetchTelemetry();
    return () => {
      isMounted.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Rebuild poll interval ──────────────────────────────────────────────────
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => { fetchTelemetry(true); resetCountdown(); }, pollInterval);
    return () => clearInterval(intervalRef.current);
  }, [pollInterval, fetchTelemetry, resetCountdown]);

  // --- UPDATE: WebSocket subscription — live circuit state updates fire
  //   immediately without waiting for the next HTTP poll. ---
  useEffect(() => {
    const handleWatchdogEvent = (msg) => {
      if (!isMounted.current) return;
      const payload = liveEventBody(msg); // --- UPDATE (v2.2): inner payload carries the circuit type
      if (payload?.type === 'circuit_half_open') { setLiveCircuitState('HALF_OPEN'); addLog('WebSocket: Circuit HALF_OPEN', 'info', payload); }

      if (payload?.type === 'circuit_open') {
        setLiveCircuitState('OPEN');
        addLog('WebSocket: Circuit OPEN', 'warn', payload);
        toast({
          title:       'ML Circuit Breaker Tripped',
          description: 'AI predictions are paused. Please inspect the engine.',
          status:      'error',
          duration:    6000,
          isClosable:  true,
        });
      }

      if (payload?.type === 'circuit_closed') {
        setLiveCircuitState('CLOSED');
        addLog('WebSocket: Circuit CLOSED — predictions resumed', 'success', payload);
        toast({
          title:       'ML Circuit Restored',
          description: 'AI predictions have resumed normal operation.',
          status:      'success',
          duration:    4000,
          isClosable:  true,
        });
      }
    };

    webSocketService?.subscribe?.('watchdog_events', handleWatchdogEvent);
    return () => webSocketService?.unsubscribe?.('watchdog_events', handleWatchdogEvent);
  }, [addLog, toast]);
  // --- END UPDATE ---

  // ── CORE: handleFlushNodeCache (preserved) ─────────────────────────────────
  const handleFlushNodeCache = useCallback(async (prefix = '') => {
    if (!isMounted.current) return;
    setIsFlushing(true);
    addLog(`Flushing Node.js cache prefix='${prefix || 'ALL'}'`, 'info');
    try {
      const res = await api.post('/ml/cache/reset', { prefix });
      toast({ title: 'Cache Reset Successful', description: res.data?.message || `Cleared Node.js cache for '${prefix || 'ALL'}'.`, status: 'success', duration: 3000, isClosable: true });
      addLog('Node cache flushed', 'success');
      fetchTelemetry(true);
    } catch (err) {
      addLog(`Node cache flush failed: ${err.message}`, 'error', err);
      toast({ title: 'Cache Reset Failed', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      if (isMounted.current) setIsFlushing(false);
    }
  }, [addLog, fetchTelemetry, toast]);

  // ── CORE: handleInvalidateModelCache (preserved) ───────────────────────────
  const handleInvalidateModelCache = useCallback((modelType) => {
    setPendingAction({
      title:        `Reload Model: ${modelType}`,
      description:  `This will force the Python engine to drop "${modelType}" from RAM and reload from disk/database. Predictions for this model will be temporarily unavailable. Continue?`,
      confirmLabel: 'Reload Now',
      scheme:       'orange',
      onConfirm:    async () => {
        onConfirmClose();
        if (!isMounted.current) return;
        setIsFlushing(true);
        addLog(`Invalidating model cache: ${modelType}`, 'info');
        try {
          const res = await api.post('/ml/cache/invalidate', { model_type: modelType });
          toast({ title: 'Model Cache Invalidated', description: res.data?.data?.message || `Successfully flushed ${modelType} from Python memory.`, status: 'success', duration: 4000, isClosable: true });
          addLog(`Model ${modelType} invalidated`, 'success');
          fetchTelemetry(true);
        } catch (err) {
          addLog(`Model invalidation failed: ${err.message}`, 'error', err);
          toast({ title: 'Invalidation Failed', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
          if (isMounted.current) setIsFlushing(false);
        }
      },
    });
    onConfirmOpen();
  }, [addLog, fetchTelemetry, onConfirmClose, onConfirmOpen, toast]);

  // ── handleFlushAllPythonCaches (preserved) ─────────────────────────────────
  const handleFlushAllPythonCaches = useCallback(() => {
    setPendingAction({
      title:        'Flush All Python Caches',
      description:  'This will clear the Model Loader cache AND the Prediction Results cache from Python RAM. All models will be cold-loaded on next request. This may cause a temporary latency spike.',
      confirmLabel: 'Flush All',
      scheme:       'red',
      onConfirm:    async () => {
        onConfirmClose();
        if (!isMounted.current) return;
        setIsFlushing(true);
        addLog('Flushing ALL Python caches', 'warn');
        try {
          const res = await api.post('/ml/cache/invalidate', { model_type: null });
          toast({ title: 'All Python Caches Flushed', description: res.data?.data?.message || 'All Python model and prediction caches have been cleared.', status: 'success', duration: 4000, isClosable: true });
          addLog('All Python caches flushed', 'success');
          fetchTelemetry(true);
        } catch (err) {
          addLog(`Full cache flush failed: ${err.message}`, 'error', err);
          toast({ title: 'Flush Failed', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
          if (isMounted.current) setIsFlushing(false);
        }
      },
    });
    onConfirmOpen();
  }, [addLog, fetchTelemetry, onConfirmClose, onConfirmOpen, toast]);

  // --- UPDATE: handleResetCircuit — sends watchdog probe to close the circuit ---
  const handleResetCircuit = useCallback(() => {
    setPendingAction({
      title:        'Reset ML Circuit Breaker',
      description:  'Force a probe scan to verify if the ML Engine is healthy again. If the probe succeeds, the circuit will close and predictions will resume.',
      confirmLabel: 'Send Probe',
      scheme:       'blue',
      onConfirm:    async () => {
        onConfirmClose();
        if (!isMounted.current) return;
        setIsFlushing(true);
        addLog('Initiating manual circuit breaker probe', 'warn');
        try {
          const res = await api.post('/workorders/watchdog/circuit/reset');
          toast({ title: 'Probe Initiated', description: res.data?.message || 'Circuit reset probe sent.', status: 'info', duration: 4000, isClosable: true });
          addLog('Circuit probe dispatched', 'info');
          setTimeout(() => fetchTelemetry(true), 1500);
        } catch (err) {
          addLog(`Circuit probe failed: ${err.message}`, 'error', err);
          toast({ title: 'Probe Failed', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
          if (isMounted.current) setIsFlushing(false);
        }
      },
    });
    onConfirmOpen();
  }, [addLog, fetchTelemetry, onConfirmClose, onConfirmOpen, toast]);
  // --- END UPDATE ---

  // --- UPDATE: handleWarmUpModels — preloads all models into RAM ---
  const handleWarmUpModels = useCallback(() => {
    setPendingAction({
      title:        'Warm Up ML Engine',
      description:  'Forces Python to preload all production models into RAM. This eliminates cold-start latency for the next prediction request.',
      confirmLabel: 'Warm Up',
      scheme:       'purple',
      onConfirm:    async () => {
        onConfirmClose();
        if (!isMounted.current) return;
        setIsFlushing(true);
        addLog('Initiating ML Engine warm-up', 'info');
        try {
          const res = await api.post('/ml/cache/warmup');
          toast({ title: 'Warm Up Complete', description: res.data?.data?.message || 'Models loaded into RAM.', status: 'success', duration: 4000, isClosable: true });
          addLog('ML engine warm-up complete', 'success');
          fetchTelemetry(true);
        } catch (err) {
          addLog(`Warm-up failed: ${err.message}`, 'error', err);
          toast({ title: 'Warm Up Failed', description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
        } finally {
          if (isMounted.current) setIsFlushing(false);
        }
      },
    });
    onConfirmOpen();
  }, [addLog, fetchTelemetry, onConfirmClose, onConfirmOpen, toast]);
  // --- END UPDATE ---

  // --- UPDATE: handleRetrainDrifted — triggers background retraining ---
  // --- UPDATE (v2.2): accepts one model or a list; each is dispatched in turn
  const handleRetrainDrifted = useCallback(async (modelTypes) => {
    if (!isMounted.current) return;
    const list = (Array.isArray(modelTypes) ? modelTypes : [modelTypes]).filter(Boolean);
    if (!list.length) return;
    setIsRetraining(true);
    let ok = 0;
    for (const modelType of list) {
      addLog(`Initiating drift retraining for: ${modelType}`, 'warn');
      const internalModelType = String(modelType).replace(/ /g, '_').toUpperCase();
      try {
        const res = await api.post('/ml/models/retrain-drifted', { model_type: internalModelType });
        ok += 1;
        toast({ title: 'Retraining Initiated', description: res.data?.data?.message || `Background retraining started for ${modelType}.`, status: 'info', duration: 5000, isClosable: true });
        addLog(`Retraining dispatched for ${modelType}`, 'success');
      } catch (err) {
        addLog(`Retraining failed: ${err.message}`, 'error', err);
        toast({ title: `Retraining Failed — ${modelType}`, description: err.response?.data?.message || err.message, status: 'error', duration: 5000, isClosable: true });
      }
    }
    if (isMounted.current) {
      setIsRetraining(false);
      if (ok) setTimeout(() => fetchTelemetry(true), 2000);
    }
  }, [addLog, toast, fetchTelemetry]);
  // --- END UPDATE
  // --- END UPDATE ---

  // ── handleExport (liveCircuitState added to payload) ──────────────────────
  const handleExport = useCallback(() => {
    try {
      // --- UPDATE: include liveCircuitState in snapshot ---
      const payload  = { timestamp: new Date().toISOString(), healthData, nodeCacheStats, liveCircuitState };
      // --- END UPDATE ---
      const dataStr  = JSON.stringify(payload, null, 2);
      const dataUri  = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
      const link     = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', `ml-telemetry-${Date.now()}.json`);
      link.click();
      addLog('Telemetry exported', 'success');
      toast({ title: 'Exported', description: 'ML telemetry snapshot saved as JSON.', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      addLog(`Export failed: ${err.message}`, 'error', err);
    }
  }, [healthData, nodeCacheStats, liveCircuitState, addLog, toast]);

  // ── Derived data (preserved) ───────────────────────────────────────────────
  // --- UPDATE (v2.2): null when nothing was reported (was 0, which read as a real 0 %)
  const overallHitRate = useMemo(() => {
    const rates = [healthData?.model_cache?.hit_rate, healthData?.prediction_cache?.hit_rate]
      .map(Number).filter(Number.isFinite);
    return rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : null;
  }, [healthData]);

  const avgGlobalLatency = useMemo(() => {
    if (!healthData?.metrics) return null;
    const metrics = Object.values(healthData.metrics).filter(m => Number.isFinite(Number(m?.avg_duration_ms)));
    if (metrics.length === 0) return null;
    return metrics.reduce((s, m) => s + Number(m.avg_duration_ms), 0) / metrics.length;
  }, [healthData]);
  // --- END UPDATE

  const latencyChartData = useMemo(() => {
    if (!healthData?.metrics) return { categories: [], series: [], colors: [] };
    const entries = Object.entries(healthData.metrics)
      .filter(([, m]) => m?.avg_duration_ms != null)
      .sort(([, a], [, b]) => b.avg_duration_ms - a.avg_duration_ms);
    return {
      categories: entries.map(([k]) => k.replace(/_/g, ' ')),
      series:     entries.map(([, m]) => +(m.avg_duration_ms.toFixed(1))),
      colors:     entries.map(([, m]) => {
        const c = getLatencyColor(m.avg_duration_ms);
        return c === 'green' ? '#48BB78' : c === 'orange' ? '#ED8936' : '#F56565';
      }),
    };
  }, [healthData]);

  const driftedModels = useMemo(() => {
    if (!healthData?.models) return [];
    return Object.entries(healthData.models)
      .filter(([modelType, details]) => modelHasDrift(details, healthData.metrics?.[modelType] || {})) // --- UPDATE (v2.2)
      .map(([modelType]) => modelType.replace(/_/g, ' '));
  }, [healthData]);

  // --- UPDATE (v2.2): FlowSense explainer — rules from the real telemetry
  const mlExplainer = useMemo(() => {
    const findings = [];
    const models = Object.entries(healthData?.models || {});
    const per = models.map(([type, d]) => {
      const m = healthData?.metrics?.[type] || {};
      return {
        model: type, status: d?.status ?? null, version: d?.active_version ?? null,
        avg_latency_ms: Number.isFinite(Number(m.avg_duration_ms)) ? Number(m.avg_duration_ms) : null,
        fallback_rate: Number.isFinite(Number(m.fallback_rate)) ? Number(m.fallback_rate) : null,
        error_rate: Number.isFinite(Number(m.error_rate)) ? Number(m.error_rate) : null,
        drift: modelHasDrift(d || {}, m), baseline_stored: !!d?.baseline_stored,
      };
    });
    if (liveCircuitState === 'OPEN') findings.push({ severity: 'high', text: 'The ML circuit breaker is OPEN — predictions are paused and rule-based fallbacks are serving.' });
    else if (liveCircuitState === 'HALF_OPEN') findings.push({ severity: 'medium', text: 'The ML circuit breaker is HALF_OPEN — the next prediction call decides whether it closes.' });
    else if (liveCircuitState === 'UNKNOWN') findings.push({ severity: 'info', text: 'The watchdog did not report a circuit state.' });
    const notReady = per.filter(p => p.status && !['ready', 'loaded', 'active'].includes(String(p.status).toLowerCase()));
    if (notReady.length) findings.push({ severity: 'high', text: `${notReady.length} model${notReady.length > 1 ? 's are' : ' is'} not ready: ${notReady.map(p => `${p.model.replace(/_/g, ' ')} (${p.status})`).join(', ')}.` });
    if (driftedModels.length) findings.push({ severity: 'high', text: `Drift / error-rate alert on ${driftedModels.join(', ')}.` });
    per.filter(p => p.fallback_rate !== null && p.fallback_rate > 0.1).forEach(p => findings.push({ severity: 'medium', text: `${p.model.replace(/_/g, ' ')} falls back on ${(p.fallback_rate * 100).toFixed(1)}% of calls.` }));
    per.filter(p => p.avg_latency_ms !== null && p.avg_latency_ms > LATENCY_THRESHOLDS.warn).forEach(p => findings.push({ severity: 'medium', text: `${p.model.replace(/_/g, ' ')} averages ${p.avg_latency_ms.toFixed(0)} ms (above ${LATENCY_THRESHOLDS.warn} ms).` }));
    const suspended = Number(healthData?.suspension_cache?.suspended_asset_count) || 0;
    if (suspended > 0) findings.push({ severity: 'medium', text: `${suspended} asset${suspended > 1 ? 's have' : ' has'} predictions suspended for lack of sensor data.` });
    if (overallHitRate !== null && overallHitRate < 0.4) findings.push({ severity: 'info', text: `Python cache hit rate is ${(overallHitRate * 100).toFixed(1)}% — warming models up may cut latency.` });
    if (healthData && !findings.some(f => ['high', 'medium'].includes(f.severity))) findings.push({ severity: 'ok', text: 'All reported models are ready with no drift, fallback or latency alerts.' });
    return {
      findings,
      facts: {
        circuit_state: liveCircuitState, models: per,
        avg_latency_ms: avgGlobalLatency, cache_hit_rate: overallHitRate,
        prediction_cache: healthData?.prediction_cache ?? null, model_cache: healthData?.model_cache ?? null,
        suspended_assets: healthData?.suspension_cache?.suspended_asset_count ?? null,
        node_cache_entries: nodeCacheStats?.size ?? null, synced_at: perfMetrics.lastUpdate,
      },
    };
  }, [healthData, nodeCacheStats, liveCircuitState, driftedModels, overallHitRate, avgGlobalLatency, perfMetrics.lastUpdate]);
  const circuitColor = CIRCUIT_COLOR[liveCircuitState] || 'gray';
  // --- END UPDATE

  // ── Latency chart options (preserved) ─────────────────────────────────────
  const latencyChartOptions = useMemo(() => ({
    chart: { type: 'bar', toolbar: { show: false }, background: 'transparent', fontFamily: 'inherit', animations: { enabled: true, easing: 'easeinout', speed: 600 } },
    plotOptions: { bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: '60%' } },
    colors: latencyChartData.colors,
    dataLabels: { enabled: true, formatter: (val) => `${val} ms`, style: { fontSize: '11px', fontWeight: 600, colors: ['#fff'] } },
    xaxis: {
      categories: latencyChartData.categories,
      labels: { style: { colors: chartText, fontSize: '11px' } },
      axisBorder: { show: false }, axisTicks: { show: false },
      title: { text: 'Average Duration (ms)', style: { color: chartText, fontSize: '12px' } },
    },
    yaxis: { labels: { style: { colors: chartText, fontSize: '12px', fontWeight: 600 } } },
    grid: { borderColor: chartGrid, strokeDashArray: 4 },
    legend: { show: false },
    tooltip: { theme: 'dark', y: { formatter: (val) => `${val} ms` } },
    annotations: {
      xaxis: [
        { x: LATENCY_THRESHOLDS.good, borderColor: '#48BB78', label: { text: 'Good',     style: { background: '#48BB78', color: '#fff', fontSize: '10px' } } },
        { x: LATENCY_THRESHOLDS.warn, borderColor: '#F56565', label: { text: 'Critical', style: { background: '#F56565', color: '#fff', fontSize: '10px' } } },
      ],
    },
  }), [latencyChartData.categories, latencyChartData.colors, chartText, chartGrid]);

  // ── Debug Console render ───────────────────────────────────────────────────
  const renderDebugConsole = () => (
    <Collapse in={showDebug} animateOpacity>
      <Box mt={4} p={4} bg={debugBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} maxH="280px" overflowY="auto">
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold" fontSize="sm">Debug Console</Text>
          <HStack>
            <Badge colorScheme="blue">Renders: {renderCount.current}</Badge>
            <Badge colorScheme="purple">Last: {perfMetrics.lastUpdate?.toLocaleTimeString() || 'N/A'}</Badge>
            <Badge colorScheme="green">{perfMetrics.renderTime}ms</Badge>
            <Button size="xs" onClick={() => setDebugLogs([])}>Clear</Button>
          </HStack>
        </HStack>
        <Divider mb={2} />
        <VStack align="stretch" spacing={1} fontSize="xs">
          {debugLogs.length === 0
            ? <Text color={mutedColor} textAlign="center">No logs — interact with the dashboard to generate entries.</Text>
            : debugLogs.map((log, i) => (
                <Box key={i} p={2} borderRadius="md"
                  bg={log.type === 'error' ? 'red.50' : log.type === 'warn' ? 'yellow.50' : log.type === 'success' ? 'green.50' : 'white'}
                  borderWidth="1px"
                  borderColor={log.type === 'error' ? 'red.200' : log.type === 'warn' ? 'yellow.200' : log.type === 'success' ? 'green.200' : 'gray.200'}
                >
                  <Text fontWeight="bold" color={log.type === 'error' ? 'red.700' : log.type === 'warn' ? 'yellow.700' : log.type === 'success' ? 'green.700' : 'gray.700'}>
                    [{new Date(log.timestamp).toLocaleTimeString()}] {log.type.toUpperCase()}: {log.message}
                  </Text>
                  {log.details && (
                    <Code mt={1} p={1} w="100%" fontSize="10px" whiteSpace="pre-wrap" display="block" colorScheme="blackAlpha">
                      {JSON.stringify(log.details, null, 2)}
                    </Code>
                  )}
                </Box>
              ))
          }
        </VStack>
      </Box>
    </Collapse>
  );

  // ── LOADING STATE ──────────────────────────────────────────────────────────
  if (loading && !healthData) {
    return (
      <VStack spacing={6} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          {[1, 2, 3, 4].map(i => <Skeleton key={i} height="120px" borderRadius="xl" />)}
        </SimpleGrid>
        <Skeleton height="300px" borderRadius="xl" />
        <Skeleton height="220px" borderRadius="xl" />
      </VStack>
    );
  }

  // ── ERROR STATE ────────────────────────────────────────────────────────────
  if (error && !healthData) {
    return (
      <Alert status="error" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" height="200px" borderRadius="xl">
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">Telemetry Connection Failed</AlertTitle>
        <AlertDescription maxWidth="sm">{error}</AlertDescription>
        <Button mt={4} colorScheme="red" onClick={() => fetchTelemetry()} leftIcon={<MdAutorenew />}>Retry Connection</Button>
      </Alert>
    );
  }

  // ── MAIN RENDER ────────────────────────────────────────────────────────────
  return (
    <Box>

      {/* DRIFT ALERT BANNER
          --- UPDATE: passes onRetrain + isRetraining --- */}
      {!driftDismissed && (
        <DriftAlertBanner
          driftedModels={driftedModels}
          onDismiss={() => setDriftDismissed(true)}
          onRetrain={handleRetrainDrifted}
          isRetraining={isRetraining}
        />
      )}
      {/* --- END UPDATE --- */}

      {/* HEADER */}
      <Flex justify="space-between" align="flex-start" mb={4} gap={4} flexWrap="wrap">
        <VStack align="flex-start" spacing={0}>
          <Heading size="md" color={headingColor}>Machine Learning Engine Telemetry</Heading>
          <Text fontSize="sm" color={textColor}>Real-time observability into Python model performance, caching, and feature drift.</Text>
        </VStack>
        <HStack spacing={2} flexWrap="wrap">
          <Select size="sm" w="80px" value={pollInterval} onChange={e => { setPollInterval(Number(e.target.value)); resetCountdown(); }} borderRadius="md">
            {POLL_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </Select>
          <Text fontSize="xs" color={mutedColor} alignSelf="center">
            {perfMetrics.lastUpdate ? `Synced ${perfMetrics.lastUpdate.toLocaleTimeString()}` : 'Not synced'}
          </Text>
          <Tooltip label="Export telemetry snapshot as JSON" hasArrow>
            <IconButton icon={<MdDownload />} size="sm" variant="outline" onClick={handleExport} isDisabled={!healthData} aria-label="Export" />
          </Tooltip>
          <Tooltip label="Toggle debug console" hasArrow>
            <IconButton icon={<MdBugReport />} size="sm" variant="outline" colorScheme={showDebug ? 'red' : 'gray'} onClick={() => setShowDebug(v => !v)} aria-label="Debug" />
          </Tooltip>
          <Tooltip label="Refresh now" hasArrow>
            <IconButton icon={<MdAutorenew />} size="sm" colorScheme="blue" variant="outline" onClick={() => { fetchTelemetry(!!healthData); resetCountdown(); }} isLoading={sectionLoading || loading} aria-label="Refresh" />
          </Tooltip>
        </HStack>
      </Flex>

      <Box mb={5}>
        <RefreshCountdown secondsLeft={countdown} pollInterval={pollInterval} />
      </Box>

      {/* KPI ROW */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>

        {/* KPI 1: Circuit Breaker
            --- UPDATE: reads liveCircuitState (WS-driven) --- */}
        <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
          borderLeft="4px solid" borderLeftColor={`${circuitColor}.400`}
          boxShadow="sm" transition="all 0.2s" _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
        >
          <Flex justify="space-between" align="flex-start">
            <Stat>
              <Text fontSize="xs" fontWeight="bold" color={mutedColor} textTransform="uppercase" mb={1}>Python ML Circuit</Text>
              <HStack spacing={2} align="center">
                <StatNumber fontSize="xl" color={`${circuitColor}.500`}>
                  {liveCircuitState}
                </StatNumber>
                <MotionBox animate={liveCircuitState === 'OPEN' ? pulseAnimation : undefined}>
                  <Icon as={liveCircuitState === 'CLOSED' ? MdCheckCircle : liveCircuitState === 'UNKNOWN' ? MdInfoOutline : MdError} color={`${circuitColor}.500`} boxSize={5} />
                </MotionBox>
              </HStack>
              <StatHelpText mb={0} mt={1} fontSize="xs">{CIRCUIT_TEXT[liveCircuitState] || CIRCUIT_TEXT.UNKNOWN}</StatHelpText>
            </Stat>
            <Box p={3} bg={`${circuitColor}.50`} _dark={{ bg: 'whiteAlpha.100' }} borderRadius="md" color={`${circuitColor}.600`}>
              <Icon as={MdOutlineHealthAndSafety} boxSize={6} />
            </Box>
          </Flex>
        </Box>
        {/* --- END UPDATE --- */}

        {/* KPI 2: Cache Hit Rate */}
        <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
          borderLeft="4px solid" borderLeftColor="blue.400"
          boxShadow="sm" transition="all 0.2s" _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
        >
          <Flex justify="space-between" align="flex-start">
            <Stat w="full">
              <Text fontSize="xs" fontWeight="bold" color={mutedColor} textTransform="uppercase" mb={1}>Python Cache Hit Rate</Text>
              <StatNumber fontSize="xl">{formatPercentage(overallHitRate)}</StatNumber>
              <Progress value={(overallHitRate ?? 0) * 100} size="sm" colorScheme={overallHitRate === null ? 'gray' : overallHitRate >= 0.7 ? 'green' : overallHitRate >= 0.4 ? 'yellow' : 'red'} mt={2} mb={1} borderRadius="full" />
              <StatHelpText mb={0} fontSize="xs">Combined LRU memory efficiency</StatHelpText>
            </Stat>
            <Box p={3} bg="blue.50" borderRadius="md" color="blue.600" ml={4}><Icon as={MdMemory} boxSize={6} /></Box>
          </Flex>
        </Box>

        {/* KPI 3: Latency */}
        <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
          borderLeft="4px solid" borderLeftColor={`${getLatencyColor(avgGlobalLatency)}.400`} /* null → gray */
          boxShadow="sm" transition="all 0.2s" _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
        >
          <Flex justify="space-between" align="flex-start">
            <Stat>
              <Text fontSize="xs" fontWeight="bold" color={mutedColor} textTransform="uppercase" mb={1}>Avg Prediction Latency</Text>
              <HStack align="baseline" spacing={1}>
                <StatNumber fontSize="xl">{avgGlobalLatency === null ? '—' : avgGlobalLatency.toFixed(1)}</StatNumber>
                <Text fontSize="sm" color={mutedColor}>ms</Text>
              </HStack>
              <StatHelpText mb={0} mt={1} fontSize="xs">{avgGlobalLatency !== null && <StatArrow type={avgGlobalLatency < 200 ? 'decrease' : 'increase'} />}Mean of per-model averages</StatHelpText>
            </Stat>
            <Box p={3} bg={avgGlobalLatency !== null && avgGlobalLatency < 300 ? 'green.50' : 'orange.50'} _dark={{ bg: 'whiteAlpha.100' }} borderRadius="md" color={avgGlobalLatency !== null && avgGlobalLatency < 300 ? 'green.600' : 'orange.600'}><Icon as={MdSpeed} boxSize={6} /></Box>
          </Flex>
        </Box>

        {/* KPI 4: Suspended */}
        <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor}
          borderLeft="4px solid" borderLeftColor={healthData?.suspension_cache?.suspended_asset_count > 0 ? 'orange.400' : 'gray.200'}
          boxShadow="sm" transition="all 0.2s" _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
        >
          <Flex justify="space-between" align="flex-start">
            <Stat>
              <Text fontSize="xs" fontWeight="bold" color={mutedColor} textTransform="uppercase" mb={1}>Suspended Predictions</Text>
              <StatNumber fontSize="xl" color={healthData?.suspension_cache?.suspended_asset_count > 0 ? 'orange.500' : headingColor}>
                {healthData?.suspension_cache?.suspended_asset_count || 0}
              </StatNumber>
              <StatHelpText mb={0} mt={1} fontSize="xs">Assets starved of sensor data</StatHelpText>
            </Stat>
            <Box p={3} bg="orange.50" borderRadius="md" color="orange.600"><Icon as={MdSensorsOff} boxSize={6} /></Box>
          </Flex>
        </Box>
      </SimpleGrid>

      {/* --- UPDATE (v2.2): FlowSense AI explainer */}
      <Box mb={6}>
        <FlowSenseAIExplainer
          module="ml_health"
          title="Explain ML engine health"
          subtitle="Models, drift, latency, caches and circuit breaker"
          facts={mlExplainer.facts}
          findings={mlExplainer.findings}
          isLoading={loading && !healthData}
          questions={[
            'Which model should I retrain first and why?',
            'Why are predictions falling back?',
            'Is it safe to flush the caches now?',
          ]}
        />
      </Box>
      {/* --- END UPDATE */}

      {/* MODEL REGISTRY + CACHE PANELS */}
      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} mb={6}>

        {/* Active Model Registry — span 2 */}
        <Box gridColumn={{ xl: 'span 2' }} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} overflow="hidden" boxShadow="sm">
          <Flex align="center" justify="space-between" p={5} borderBottomWidth="1px" borderColor={borderColor} bg={theadBg}>
            <HStack spacing={2}>
              <Icon as={MdOutlineModelTraining} color="blue.500" boxSize={5} />
              <Heading size="sm" color={headingColor}>Active Production Models</Heading>
              {sectionLoading && <Skeleton w="60px" h="14px" borderRadius="full" />}
            </HStack>
            <Tooltip label="Models currently loaded in Python RAM" hasArrow>
              <Icon as={MdInfoOutline} color={mutedColor} cursor="default" />
            </Tooltip>
          </Flex>
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Model Type</Th><Th>Status</Th><Th>Active Version</Th>
                  <Th isNumeric>Avg Latency</Th><Th isNumeric>Fallback Rate</Th>
                  <Th>Feature Drift</Th><Th textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {healthData?.models && Object.entries(healthData.models).map(([modelType, details]) => {
                  const metrics  = healthData.metrics?.[modelType] || {};
                  const isReady  = details.status === 'ready';
                  const hasDrift = modelHasDrift(details, metrics); // --- UPDATE (v2.2)
                  const latColor = getLatencyColor(metrics.avg_duration_ms);
                  return (
                    <Tr key={modelType} _hover={{ bg: tableHoverBg }} transition="background 0.15s">
                      <Td fontWeight="semibold" fontSize="sm">{modelType.replace(/_/g, ' ')}</Td>
                      <Td><StatusBadge status={details.status} /></Td>
                      <Td>{isReady ? <Code fontSize="xs" borderRadius="md" px={1}>{details.active_version}</Code> : <Text color={mutedColor} fontSize="xs">—</Text>}</Td>
                      <Td isNumeric>{metrics.avg_duration_ms != null ? <Badge colorScheme={latColor} variant="subtle" fontSize="xs">{metrics.avg_duration_ms} ms</Badge> : <Text color={mutedColor} fontSize="xs">—</Text>}</Td>
                      <Td isNumeric>
                        {metrics.fallback_rate != null ? (
                          <HStack justify="flex-end" spacing={1}>
                            <Text color={metrics.fallback_rate > 0.1 ? 'red.500' : textColor} fontSize="sm">{formatPercentage(metrics.fallback_rate)}</Text>
                            {metrics.fallback_rate > 0.1 && <Icon as={MdWarning} color="red.500" boxSize={3} />}
                          </HStack>
                        ) : <Text color={mutedColor} fontSize="xs" textAlign="right">—</Text>}
                      </Td>
                      <Td>
                        {details.baseline_stored
                          ? <Badge colorScheme={hasDrift ? 'orange' : 'green'} variant="subtle" borderRadius="full" px={2}>{hasDrift ? '⚠ DRIFT' : '✓ ALIGNED'}</Badge>
                          : <Text fontSize="xs" color={mutedColor}>No Baseline</Text>
                        }
                      </Td>
                      <Td textAlign="right">
                        {/* --- UPDATE (v2.2): retrain a flagged model from its row */}
                        {hasDrift && (
                          <Button size="xs" variant="ghost" colorScheme="orange" mr={1} onClick={() => handleRetrainDrifted(modelType.replace(/_/g, ' '))} isLoading={isRetraining}>
                            Retrain
                          </Button>
                        )}
                        <Tooltip label={`Force Python to flush ${modelType} from RAM and reload.`} hasArrow>
                          <Button size="xs" variant="ghost" colorScheme="blue" leftIcon={<MdAutorenew />} onClick={() => handleInvalidateModelCache(modelType)} isLoading={isFlushing} isDisabled={!isReady}>
                            Reload
                          </Button>
                        </Tooltip>
                      </Td>
                    </Tr>
                  );
                })}
                {(!healthData?.models || Object.keys(healthData.models).length === 0) && (
                  <Tr><Td colSpan={7} textAlign="center" py={8} color={mutedColor} fontSize="sm">No active models found in Python registry.</Td></Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* Cache panels — span 1 */}
        <VStack spacing={6} align="stretch">

          {/* Python Prediction Cache */}
          <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
            <HStack mb={4} spacing={2}>
              <Icon as={MdStorage} color="purple.500" />
              <Heading size="sm" color={headingColor}>Python Prediction Cache</Heading>
            </HStack>
            <VStack align="stretch" spacing={3}>
              {[
                { label: 'Items in RAM',  value: healthData?.prediction_cache?.size   || 0, color: headingColor  },
                { label: 'Cache Hits',    value: healthData?.prediction_cache?.hits   || 0, color: 'green.500'  },
                { label: 'Cache Misses',  value: healthData?.prediction_cache?.misses || 0, color: 'orange.500' },
              ].map(({ label, value, color }) => (
                <Flex key={label} justify="space-between" align="center" p={2} borderRadius="md" bg={theadBg}>
                  <Text fontSize="sm" color={textColor}>{label}</Text>
                  <Text fontWeight="bold" color={color}>{value}</Text>
                </Flex>
              ))}
              <Divider />
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={mutedColor}>Model Loader Hit Rate</Text>
                <Badge colorScheme={healthData?.model_cache?.hit_rate > 0.5 ? 'green' : 'orange'}>{formatPercentage(healthData?.model_cache?.hit_rate)}</Badge>
              </Flex>
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" color={mutedColor}>Models Loaded</Text>
                <Badge colorScheme="blue">{healthData?.model_cache?.size || 0}</Badge>
              </Flex>
              <Divider />
              {/* --- UPDATE: Warm Up + Reset Circuit buttons ---
                  Ordered: least destructive → most destructive             */}
              <Button size="sm" colorScheme="purple" variant="outline" w="full" leftIcon={<MdOutlineWhatshot />} onClick={handleWarmUpModels} isLoading={isFlushing}>
                Warm Up Models
              </Button>
              <Button size="sm" colorScheme="blue" variant="outline" w="full" leftIcon={<MdPowerSettingsNew />} onClick={handleResetCircuit} isLoading={isFlushing}>
                Reset ML Circuit Breaker
              </Button>
              {/* --- END UPDATE --- */}
              <Button size="sm" colorScheme="red" variant="outline" w="full" leftIcon={<MdDeleteSweep />} onClick={handleFlushAllPythonCaches} isLoading={isFlushing}>
                Flush All Python Caches
              </Button>
            </VStack>
          </Box>

          {/* Node.js Response Cache */}
          <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
            <HStack justify="space-between" mb={4}>
              <HStack spacing={2}>
                <Icon as={MdTimeline} color="teal.500" />
                <Heading size="sm" color={headingColor}>Node.js Response Cache</Heading>
              </HStack>
              <Badge colorScheme={nodeCacheStats ? 'green' : 'gray'} variant="subtle">{nodeCacheStats ? 'ONLINE' : 'FETCHING'}</Badge>
            </HStack>
            <VStack align="stretch" spacing={3}>
              <Flex justify="space-between" align="center" p={2} borderRadius="md" bg={theadBg}>
                <Text fontSize="sm" color={textColor}>Active Endpoints Cached</Text>
                <Text fontWeight="bold">{nodeCacheStats?.size || 0}</Text>
              </Flex>
              <Box>
                <Text fontSize="xs" fontWeight="bold" color={mutedColor} textTransform="uppercase" mb={2}>Targeted Flush</Text>
                <Wrap spacing={2}>
                  {[
                    { label: 'RUL Scores',  prefix: 'rulScores'             },
                    { label: 'AI Insights', prefix: 'networkAIInsights'     },
                    { label: 'Risk Maps',   prefix: 'riskMap'               },
                    { label: 'Priorities',  prefix: 'maintenancePriorities' },
                  ].map(({ label, prefix }) => (
                    <WrapItem key={prefix}>
                      <Button size="xs" variant="outline" colorScheme="teal" onClick={() => handleFlushNodeCache(prefix)} isLoading={isFlushing}>{label}</Button>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
              <Button size="sm" colorScheme="teal" leftIcon={<MdDeleteSweep />} onClick={() => handleFlushNodeCache('')} isLoading={isFlushing} w="full">
                Flush All Node Caches
              </Button>
            </VStack>
          </Box>
        </VStack>
      </SimpleGrid>

      {/* LATENCY CHART + SUSPENSION DETAILS */}
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={6}>

        <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
          <HStack mb={4} spacing={2}>
            <Icon as={FaChartBar} color="blue.500" />
            <Heading size="sm" color={headingColor}>Per-Model Latency Breakdown</Heading>
            <Tooltip label="Green ≤150ms · Orange ≤400ms · Red >400ms" hasArrow>
              <Icon as={MdInfoOutline} color={mutedColor} boxSize={4} cursor="default" />
            </Tooltip>
          </HStack>
          {latencyChartData.categories.length > 0 ? (
            <ReactApexChart options={latencyChartOptions} series={[{ name: 'Avg Latency', data: latencyChartData.series }]} type="bar" height={220} />
          ) : (
            <Flex justify="center" align="center" h="180px" bg={theadBg} borderRadius="lg">
              <VStack spacing={2}><Icon as={FaChartBar} boxSize={8} color="gray.300" /><Text fontSize="sm" color={mutedColor}>No latency metrics available</Text></VStack>
            </Flex>
          )}
        </Box>

        <Box bg={cardBg} p={5} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
          <HStack justify="space-between" mb={4}>
            <HStack spacing={2}><Icon as={MdSensorsOff} color="orange.500" /><Heading size="sm" color={headingColor}>Suspension Cache Detail</Heading></HStack>
            <HStack spacing={2}>
              <Badge colorScheme={healthData?.suspension_cache?.suspended_asset_count > 0 ? 'orange' : 'green'}>{healthData?.suspension_cache?.suspended_asset_count || 0} suspended</Badge>
              {healthData?.suspension_cache?.suspended_asset_count > 0 && (
                <IconButton icon={<MdExpandMore style={{ transform: expandSuspended ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />} size="xs" variant="ghost" onClick={() => setExpandSuspended(v => !v)} aria-label="Toggle" />
              )}
            </HStack>
          </HStack>
          <VStack align="stretch" spacing={3}>
            {[
              { label: 'Total Suspended', value: healthData?.suspension_cache?.suspended_asset_count || 0 },
              { label: 'Cache TTL',       value: healthData?.suspension_cache?.ttl_hours ? `${healthData.suspension_cache.ttl_hours}h` : 'N/A' },
              { label: 'Last Eviction',   value: healthData?.suspension_cache?.last_eviction ? new Date(healthData.suspension_cache.last_eviction).toLocaleTimeString() : 'N/A' },
            ].map(({ label, value }) => (
              <Flex key={label} justify="space-between" align="center" p={2} borderRadius="md" bg={theadBg}>
                <Text fontSize="sm" color={textColor}>{label}</Text>
                <Text fontWeight="bold" color={headingColor}>{value}</Text>
              </Flex>
            ))}
            <Collapse in={expandSuspended} animateOpacity>
              {healthData?.suspension_cache?.suspended_assets?.length > 0 ? (
                <TableContainer maxH="140px" overflowY="auto" mt={2} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Table size="xs" variant="simple">
                    <Thead bg={theadBg}><Tr><Th>Asset ID</Th><Th>Suspended At</Th></Tr></Thead>
                    <Tbody>
                      {healthData.suspension_cache.suspended_assets.map((entry, idx) => (
                        <Tr key={idx} _hover={{ bg: tableHoverBg }}>
                          <Td><Code fontSize="xs">{entry.asset_id || entry}</Code></Td>
                          <Td fontSize="xs" color={mutedColor}>{entry.suspended_at ? new Date(entry.suspended_at).toLocaleTimeString() : '—'}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Text fontSize="xs" color={mutedColor} textAlign="center" py={3}>No individual asset records in suspension cache payload.</Text>
              )}
            </Collapse>
            {healthData?.suspension_cache?.suspended_asset_count === 0 && (
              <Flex align="center" justify="center" p={4} bg="green.50" borderRadius="lg" gap={2}>
                <Icon as={MdCheckCircle} color="green.500" />
                <Text fontSize="sm" color="green.700" fontWeight="medium">All assets have sufficient sensor data</Text>
              </Flex>
            )}
          </VStack>
        </Box>
      </SimpleGrid>

      {renderDebugConsole()}

      <ConfirmModal isOpen={isConfirmOpen} onClose={onConfirmClose} action={pendingAction} isLoading={isFlushing} />

    </Box>
  );
};

export default MlHealthDashboard;